import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiLock, FiMail, FiKey, FiEye, FiEyeOff, FiArrowRight, FiCheckCircle, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import { authAPI } from '../../services/api';
import './auth.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');
    if (emailParam) setEmail(emailParam);
    if (tokenParam) setToken(tokenParam);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword({
        email: email.trim(),
        token: token.trim(),
        newPassword,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Code invalide ou expiré. Veuillez refaire une demande.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      {/* Panneau gauche */}
      <div className="auth-left">
        <div className="auth-left-content">
          <div className="auth-brand">
            <div className="auth-brand-icon">🏢</div>
            <div className="auth-brand-name">Gestion<span>Immo</span></div>
          </div>
          <h2>Choisissez un nouveau mot de passe robuste</h2>
          <p>
            Votre mot de passe protège vos données immobilières sensibles, contrats et encaissements de loyers.
          </p>
          <div style={{ marginTop: 24, padding: 16, background: 'rgba(255,255,255,0.08)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
              💡 Bonnes pratiques :
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, color: 'rgba(255,255,255,0.75)', fontSize: 12, lineHeight: 1.6 }}>
              <li>Au moins 6 caractères (8 ou plus recommandé)</li>
              <li>Combinez lettres, chiffres et caractères spéciaux</li>
              <li>Ne partagez jamais vos accès d'administration</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Panneau droit */}
      <div className="auth-right">
        <div className="auth-right-header">
          <h1>Nouveau mot de passe 🔒</h1>
          <p>Saisissez votre code de vérification et votre nouveau mot de passe</p>
        </div>

        {error && (
          <div className="auth-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertCircle size={16} />
            {error}
          </div>
        )}

        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto', color: '#16a34a' }}>
              <FiCheckCircle size={36} />
            </div>
            <h3 style={{ fontSize: 18, color: '#1e293b', marginBottom: 8 }}>Mot de passe réinitialisé !</h3>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, marginBottom: 24 }}>
              Votre mot de passe a été mis à jour avec succès. Vous pouvez maintenant vous connecter avec vos nouveaux identifiants.
            </p>

            <button
              type="button"
              className="auth-btn-primary"
              onClick={() => navigate('/login')}
            >
              Se connecter maintenant
              <FiArrowRight size={18} />
            </button>
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Adresse email</label>
              <div className="auth-input-wrapper">
                <FiMail size={16} className="auth-input-icon" />
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Code de vérification (6 chiffres ou jeton reçu)</label>
              <div className="auth-input-wrapper">
                <FiKey size={16} className="auth-input-icon" />
                <input
                  type="text"
                  className="form-control"
                  value={token}
                  onChange={e => setToken(e.target.value)}
                  placeholder="Ex: 584920"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Nouveau mot de passe</label>
              <div className="auth-input-wrapper">
                <FiLock size={16} className="auth-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Confirmer le nouveau mot de passe</label>
              <div className="auth-input-wrapper">
                <FiLock size={16} className="auth-input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-btn-primary" disabled={loading} style={{ marginTop: 10 }}>
              {loading ? (
                <>
                  <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                  Mise à jour en cours...
                </>
              ) : (
                <>
                  Enregistrer le mot de passe
                  <FiArrowRight size={18} />
                </>
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: 24, display: 'flex', justifyContent: 'center', gap: 16 }}>
              <Link to="/forgot-password" style={{ color: '#64748b', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}>
                Renvoyer un code
              </Link>
              <span style={{ color: '#cbd5e1' }}>•</span>
              <Link to="/login" style={{ color: '#7c3aed', textDecoration: 'none', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <FiArrowLeft size={14} /> Connexion
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
